import { MonthPickerDialogViewModel } from '@common/Components/MonthPickerDialog';
import MonthPickerDialog from '@common/Components/MonthPickerDialog.vue';
import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue';
import { MonthYear, MonthYearHelpers, ProjectAcceptance } from "@common/types";
import { ICommandBase } from "@Quest/cqrs";
import { AddProjectsDialogViewModel } from '@Quest/PortfolioScenarios/Components/AddProjectsDialog';
import AddProjectsDialog from '@Quest/PortfolioScenarios/Components/AddProjectsDialog.vue';
import PortfolioScenarioNavigation from '@Quest/PortfolioScenarios/Components/PortfolioScenarioNavigation.vue';
import { AddProjectsToScenarioCommandV1, LockProjectScenarioAcceptanceCommandV1, LockProjectScenarioCommitmentDateCommandV1, LockProjectScenarioStartDateCommandV1, OptimizeSelectionCommandV1, RemoveProjectsFromScenarioCommandV1, UnlockProjectScenarioAcceptanceCommandV1, UnlockProjectScenarioCommitmentDateCommandV1, UnlockProjectScenarioStartDateCommandV1, UpdatePortfolioScenarioCommandV1, UpdateProjectScenarioAcceptanceCommandV1, UpdateProjectScenarioCommitmentDateCommandV1, UpdateProjectScenarioMaxStartDateCommandV1, UpdateProjectScenarioMinStartDateCommandV1, UpdateProjectScenarioPlanLockCommandV1, UpdateProjectScenarioStartDateCommandV1 } from '@Quest/PortfolioScenarios/Domain/commands-core';
import PortfolioScenariosCommands from '@Quest/PortfolioScenarios/Domain/commands-vue-component';
import PortfolioScenariosQueries from '@Quest/PortfolioScenarios/Domain/queries-vue-component';
import { ProjectsPageReadModel, ScenarioProjectPlanPickerReadModel, ScenarioProjectReadModel } from '@Quest/PortfolioScenarios/Domain/readmodels';
import { Component, mixins } from 'vue-facing-decorator';
import { ProjectPlanPickerDialogViewModel } from '@Quest/PortfolioScenarios/Components/ProjectPlanPickerDialog';
import ProjectPlanPickerDialog from '@Quest/PortfolioScenarios/Components/ProjectPlanPickerDialog.vue';


@Component({ components: { PortfolioNavigation, AddProjectsDialog, MonthPickerDialog, PortfolioScenarioNavigation, ProjectPlanPickerDialog } })
export default class ProjectsPage extends mixins(PortfolioScenariosQueries, PortfolioScenariosCommands)
{
    get workspaceId(): number { return Number.parseInt(this.$route.params.workspaceId); }
    get tchProjectId(): number { return Number.parseInt(this.$route.params.tchProjectId); }
    get scenarioId() { return this.$route.params.scenarioId; }
    get portfolioId()
    {
        if (!this.rm?.PortfolioId)
        {
            throw new Error('PortfolioId is not defined.');
        }
        return this.rm.PortfolioId;
    }

    rm: ProjectsPageReadModel | null = null;

    searchPhrase: string | null = null;
    acceptanceFilter: ProjectAcceptance | null = null;
    kanbanFilter: string | null = null;
    batchOperationEntities: any[] = [];

    addProjectsDialog = new AddProjectsDialogViewModel();
    startDateMonthPicker = new MonthPickerDialogViewModel();
    minStartDateMonthPicker = new MonthPickerDialogViewModel();
    maxStartDateMonthPicker = new MonthPickerDialogViewModel();
    commitmentDateMonthPicker = new MonthPickerDialogViewModel();
    projectPlanPickerDialog = new ProjectPlanPickerDialogViewModel();

    get noneSelected(): boolean { return !this.rm!.Projects.some(a => a.UiSelected); }
    get allSelected(): boolean { return !this.rm!.Projects.some(a => !a.UiSelected); }
    get anySelected(): boolean { return this.rm!.Projects.some(a => a.UiSelected); }
    get selected(): ScenarioProjectReadModel[] { return this.rm!.Projects.filter(a => a.UiSelected) || [] }
    get projectsView(): ScenarioProjectReadModel[]
    {
        let view = new Array(...this.rm!.Projects);

        if (this.searchPhrase)
        {
            const phrase = this.searchPhrase.toLowerCase();
            view = view.filter(a => a.Name.toLowerCase().indexOf(phrase) > -1);
        }

        if (this.acceptanceFilter)
        {
            return view.filter(a => a.Acceptance === this.acceptanceFilter);
        }

        if (this.kanbanFilter)
        {
            return view.filter(a => a.KanbanStatus === this.kanbanFilter);
        }

        return view;
    }


    get filterLabel(): string
    {
        if (this.kanbanFilter) return this.kanbanFilter;
        if (this.acceptanceFilter) return this.acceptanceFilter;
        return 'All';
    }


    selectAll()
    {
        this.projectsView.forEach(a => a.UiSelected = true);
    }

    unselectAll()
    {
        this.rm!.Projects.forEach(a => a.UiSelected = false);
    }

    setFilter(acceptanceFilter: ProjectAcceptance | null, kanbanFilter: string | null)
    {
        this.kanbanFilter = kanbanFilter;
        this.acceptanceFilter = acceptanceFilter;
    }



    showAddProjectsDialog()
    {
        this.addProjectsDialog.show(this.rm!.Lists, this.rm!.Statuses);
    }

    showPlanPicker(p: ScenarioProjectReadModel)
    {
        if (p.Plans.length)
        {
            this.projectPlanPickerDialog.show(p.Id, p.Name, p.ActivePlanId, p.LockedPlanId);
            this.projectPlanPickerDialog.setData(p.Plans);
        }
        else
        {
            this.runScenarioProjectPlanPickerQueryV1(this.workspaceId, this.tchProjectId, this.scenarioId, p.Id);
            this.projectPlanPickerDialog.show(p.Id, p.Name, p.ActivePlanId, p.LockedPlanId);
        }
    }

    showStartDateMonthPicker(project: ScenarioProjectReadModel | null) 
    {
        this.batchOperationEntities = project != null ? [project.Id] : this.selected.map(a => a.Id);

        const startDate = project?.StartDate ?? project?.DefaultStartDate ?? null;

        this.startDateMonthPicker.show(MonthYearHelpers.buildFromMonthYearString(startDate), 'Edit start date');
    }

    showMinStartDateMonthPicker(project: ScenarioProjectReadModel | null) 
    {
        this.batchOperationEntities = project != null ? [project.Id] : this.selected.map(a => a.Id);

        this.minStartDateMonthPicker.show(MonthYearHelpers.buildFromMonthYearString(project?.MinStartDate ?? null), 'Edit min start date');
    }

    showMaxStartDateMonthPicker(project: ScenarioProjectReadModel | null) 
    {
        this.batchOperationEntities = project != null ? [project.Id] : this.selected.map(a => a.Id);

        this.maxStartDateMonthPicker.show(MonthYearHelpers.buildFromMonthYearString(project?.MaxStartDate ?? null), 'Edit max start date');
    }

    showCommitmentDateMonthPicker(project: ScenarioProjectReadModel | null) 
    {
        this.batchOperationEntities = project != null ? [project.Id] : this.selected.map(a => a.Id);

        this.commitmentDateMonthPicker.show(MonthYearHelpers.buildFromMonthYearString(project?.CommitmentDate ?? null), 'Edit commitment date');
    }


    handleUpdateProjectScenarioPlanLockCommand(e: { projectId: string, lockedPlanId: string | null } )
    {
        this.invokeUpdateProjectScenarioPlanLockCommandV1(this.workspaceId, this.portfolioId, this.scenarioId, e.projectId, e.lockedPlanId);
    }

    handleLockProjectScenarioAcceptanceCommand(projectId: string | null)
    {
        const ids = projectId != null ? [projectId] : this.selected.map(a => a.Id);
        this.invokeLockProjectScenarioAcceptanceCommandV1(this.workspaceId, this.portfolioId, this.scenarioId, ids);
    }

    handleUnlockProjectScenarioAcceptanceCommand(projectId: string | null)
    {
        const ids = projectId != null ? [projectId] : this.selected.map(a => a.Id);
        this.invokeUnlockProjectScenarioAcceptanceCommandV1(this.workspaceId, this.portfolioId, this.scenarioId, ids);
    }

    handleLockProjectScenarioStartDateCommand(projectId: string | null)
    {
        const ids = projectId != null ? [projectId] : this.selected.map(a => a.Id);
        this.invokeLockProjectScenarioStartDateCommandV1(this.workspaceId, this.portfolioId, this.scenarioId, ids);
    }

    handleUnlockProjectScenarioStartDateCommand(projectId: string | null)
    {
        const ids = projectId != null ? [projectId] : this.selected.map(a => a.Id);
        this.invokeUnlockProjectScenarioStartDateCommandV1(this.workspaceId, this.portfolioId, this.scenarioId, ids);
    }

    handleLockProjectScenarioCommitmentDateCommand(projectId: string | null)
    {
        const ids = projectId != null ? [projectId] : this.selected.map(a => a.Id);
        this.invokeLockProjectScenarioCommitmentDateCommandV1(this.workspaceId, this.portfolioId, this.scenarioId, ids);
    }

    handleUnlockProjectScenarioCommitmentDateCommand(projectId: string | null)
    {
        const ids = projectId != null ? [projectId] : this.selected.map(a => a.Id);
        this.invokeUnlockProjectScenarioCommitmentDateCommandV1(this.workspaceId, this.portfolioId, this.scenarioId, ids);
    }


    handleUpdateProjectScenarioStartDateCommand(date: MonthYear | null)
    {
        this.invokeUpdateProjectScenarioStartDateCommandV1(this.workspaceId, this.portfolioId, this.scenarioId, this.batchOperationEntities, date);
    }

    handleUpdateProjectScenarioMinStartDateCommand(date: MonthYear | null)
    {
        this.invokeUpdateProjectScenarioMinStartDateCommandV1(this.workspaceId, this.portfolioId, this.scenarioId, this.batchOperationEntities, date);
    }

    handleUpdateProjectScenarioMaxStartDateCommand(date: MonthYear | null)
    {
        this.invokeUpdateProjectScenarioMaxStartDateCommandV1(this.workspaceId, this.portfolioId, this.scenarioId, this.batchOperationEntities, date);
    }

    handleUpdateProjectScenarioCommitmentDateCommand(date: MonthYear | null)
    {
        this.invokeUpdateProjectScenarioCommitmentDateCommandV1(this.workspaceId, this.portfolioId, this.scenarioId, this.batchOperationEntities, date);
    }

    handleAddProjectsToScenarioCommand(viewDefId: string | null, statusId: number | null)
    {
        this.invokeAddProjectsToScenarioCommandV1(this.workspaceId, this.portfolioId, this.scenarioId, viewDefId, statusId);
    }

    handleRemoveProjectsFromScenarioCommand(ids: string[] | null)
    {
        if (confirm("Are you sure you want to remove the selected items from this scenario?"))
        {
            ids = ids ?? this.selected.map(a => a.Id);
            this.invokeRemoveProjectsFromScenarioCommandV1(this.workspaceId, this.portfolioId, this.scenarioId, ids);
        }
    }

    handleUpdateProjectScenarioAcceptanceCommand(acceptance: string, id: string | null)
    {
        const ids = id != null ? [id] : this.selected.map(a => a.Id);
        this.invokeUpdateProjectScenarioAcceptanceCommandV1(this.workspaceId, this.portfolioId, this.scenarioId, ids, acceptance);
    }


    mounted()
    {
        document.title = "Portfolio Plan - Projects";
        this.runProjectsPageQueryV1(this.workspaceId, this.tchProjectId, this.scenarioId);
    }

    onSetModel(model: any, modelName: string) 
    {
        if (modelName === "ProjectsPageReadModel")
        {
            this.rm = model as ProjectsPageReadModel;
        }
        else if (modelName === "ScenarioProjectPlanPickerReadModel")
        {
            const rm = model as ScenarioProjectPlanPickerReadModel;

            const p = this.rm!.Projects.find(a => a.Id === rm.ProjectId);
            p.Plans = rm.Plans;
            this.projectPlanPickerDialog.setData(rm.Plans);
        }
    }

    onCommandSucceed(cmd: ICommandBase, correlationId: string, result: any)
    {
        if (AddProjectsToScenarioCommandV1.matchType(cmd))
        {
            this.runProjectsPageQueryV1(this.workspaceId, this.tchProjectId, this.scenarioId);
            this.addProjectsDialog.close();
        }
        else if (RemoveProjectsFromScenarioCommandV1.matchType(cmd))
        {
            const cmd1 = cmd as RemoveProjectsFromScenarioCommandV1;
            this.rm!.Projects = this.rm!.Projects.filter(p => !cmd1.ProjectIds.includes(p.Id));
        }

        else if (UpdatePortfolioScenarioCommandV1.matchType(cmd))
        {
            this.runProjectsPageQueryV1(this.workspaceId, this.tchProjectId, this.scenarioId);
        }
        else if (OptimizeSelectionCommandV1.matchType(cmd))
        {
            this.runProjectsPageQueryV1(this.workspaceId, this.tchProjectId, this.scenarioId);
        }


        else if (UpdateProjectScenarioAcceptanceCommandV1.matchType(cmd))
        {
            const cmd1 = cmd as UpdateProjectScenarioAcceptanceCommandV1;
            const ids = (result as string).split(';');
            this.rm!.Projects.filter(p => ids.includes(p.Id)).forEach(p => p.Acceptance = cmd1.Acceptance as ProjectAcceptance);
        }
        else if (LockProjectScenarioAcceptanceCommandV1.matchType(cmd))
        {
            const ids = (result as string).split(';');
            this.rm!.Projects.filter(p => ids.includes(p.Id)).forEach(p => p.AcceptanceLocked = true);
        }
        else if (UnlockProjectScenarioAcceptanceCommandV1.matchType(cmd))
        {
            const ids = (result as string).split(';');
            this.rm!.Projects.filter(p => ids.includes(p.Id)).forEach(p => p.AcceptanceLocked = false);
        }

        else if (UpdateProjectScenarioCommitmentDateCommandV1.matchType(cmd))
        {
            const cmd1 = cmd as UpdateProjectScenarioCommitmentDateCommandV1;
            const ids = (result as string).split(';');
            const commitmentDate = MonthYearHelpers.toMonthYearString(cmd1.CommitmentDate);

            this.rm!.Projects.filter(p => ids.includes(p.Id)).forEach(p => p.CommitmentDate = commitmentDate);
        }
        else if (LockProjectScenarioCommitmentDateCommandV1.matchType(cmd))
        {
            const ids = (result as string).split(';');
            this.rm!.Projects.filter(p => ids.includes(p.Id)).forEach(p => p.CommitmentDateLocked = true);
        }
        else if (UnlockProjectScenarioCommitmentDateCommandV1.matchType(cmd))
        {
            const ids = (result as string).split(';');
            this.rm!.Projects.filter(p => ids.includes(p.Id)).forEach(p => p.CommitmentDateLocked = false);
        }

        else if (UpdateProjectScenarioStartDateCommandV1.matchType(cmd))
        {
            const cmd1 = cmd as UpdateProjectScenarioStartDateCommandV1;
            const ids = (result as string).split(';');
            const startDate = MonthYearHelpers.toMonthYearString(cmd1.StartDate);

            this.rm!.Projects.filter(p => ids.includes(p.Id)).forEach(p => p.StartDate = startDate);
        }
        else if (LockProjectScenarioStartDateCommandV1.matchType(cmd))
        {
            const ids = (result as string).split(';');
            this.rm!.Projects.filter(p => ids.includes(p.Id)).forEach(p => p.StartDateLocked = true);
        }
        else if (UnlockProjectScenarioStartDateCommandV1.matchType(cmd))
        {
            const ids = (result as string).split(';');
            this.rm!.Projects.filter(p => ids.includes(p.Id)).forEach(p => p.StartDateLocked = false);
        }

        else if (UpdateProjectScenarioMinStartDateCommandV1.matchType(cmd))
        {
            const cmd1 = cmd as UpdateProjectScenarioMinStartDateCommandV1;
            const ids = (result as string).split(';');
            const minStartDate = MonthYearHelpers.toMonthYearString(cmd1.MinStartDate);

            this.rm!.Projects.filter(p => ids.includes(p.Id)).forEach(p => p.MinStartDate = minStartDate);
        }
        else if (UpdateProjectScenarioMaxStartDateCommandV1.matchType(cmd))
        {
            const cmd1 = cmd as UpdateProjectScenarioMaxStartDateCommandV1;
            const ids = (result as string).split(';');
            const maxStartDate = MonthYearHelpers.toMonthYearString(cmd1.MaxStartDate);

            this.rm!.Projects.filter(p => ids.includes(p.Id)).forEach(p => p.MaxStartDate = maxStartDate);
        }
        else if (UpdateProjectScenarioPlanLockCommandV1.matchType(cmd))
        {
            const cmd1 = cmd as UpdateProjectScenarioPlanLockCommandV1;
            const projectId = cmd1.ProjectId;
            const lockedPlanId = cmd1.LockedPlanId ?? null;

            const project = this.rm!.Projects.find(a => a.Id === projectId);
            project.LockedPlanId = lockedPlanId;
            project.PlanId = project.LockedPlanId ?? project.ActivePlanId;

            const plan = project.Plans?.find(p => p.Id === project.PlanId);
            project.PlanName = plan?.Name ?? null;

            this.projectPlanPickerDialog.close();
        }
    }
}